import { signal, effect } from "alien-signals";
import {
  calculatePageData,
  pageData,
  pdfSize,
  scale,
} from "../view-models/page-signals.js";
import { TextLayer, pdfDocument } from "../view-models/book-signals.js";
import {
  viewportHeight,
  viewportWidth,
} from "../view-models/platform/viewport-signals.js";

export const pageElements = {
  start: signal(),
  end: signal(),
  root: signal(),
};

effect(() => {
  const root = pageElements.root();
  const data = pageData();
  if (root) {
    root.data = data;
  }
});

// Every render request that has not settled yet, so that callers (tests,
// and anything that needs a painted page) can wait for the reader to be
// idle. Each page element gets a chain of renders: PDF.js refuses two
// concurrent render() calls on one canvas, and a request that has been
// superseded by a newer one for the same element is skipped rather than
// painted, so rapid page turns do not queue up stale work.
const pendingRenders = new Set();
const elementRenders = new WeakMap();

/** Resolves once no page render is queued or in progress. */
export async function renderComplete() {
  while (pendingRenders.size > 0) {
    await Promise.allSettled([...pendingRenders]);
  }
}

export function renderPage(element, pageNumber, calculateViewport) {
  if (!element) return Promise.resolve();
  let state = elementRenders.get(element);
  if (!state) {
    state = { generation: 0, chain: Promise.resolve() };
    elementRenders.set(element, state);
  }
  const generation = ++state.generation;
  const task = state.chain.then(() => {
    if (generation !== state.generation) return;
    return drawPage(element, pageNumber, calculateViewport);
  });
  state.chain = task.catch(() => {});
  pendingRenders.add(task);
  const settle = () => pendingRenders.delete(task);
  task.then(settle, settle);
  return task;
}

function clearPage(element) {
  const canvas = element.querySelector(".cassis-pdf-canvas-layer");
  const text = element.querySelector(".cassis-pdf-text-layer");
  canvas?.getContext("2d")?.clearRect(0, 0, canvas.width, canvas.height);
  text?.replaceChildren();
  element.rendered = false;
  element.renderedPage = null;
}

async function drawPage(element, pageNumber, calculateViewport) {
  const pdf = pdfDocument();
  if (!element.ratio || !pdf || !Number.isInteger(pageNumber)) return;
  if (pageNumber < 1 || pageNumber > pdf.numPages) {
    // An end page past the last page of an odd-length document, or a stale
    // page number from a previous document: show nothing rather than ask
    // PDF.js for a page that does not exist.
    clearPage(element);
    return;
  }
  let ratio = element.ratio;
  element.rendered = false;
  try {
    const page = await pdf.getPage(pageNumber);
    if (calculateViewport) {
      const SCALE = 1.0;
      const viewport = page.getViewport({ scale: SCALE });
      const oldViewport = pdfSize();
      if (
        viewport.height !== oldViewport.height ||
        viewport.width !== oldViewport.width
      ) {
        pdfSize(viewport);
        const calculations = calculatePageData(
          viewport,
          viewportHeight(),
          viewportWidth(),
          scale(),
        );
        ratio = calculations.ratio;
      }
    }
    const canvas = element.querySelector(".cassis-pdf-canvas-layer");
    const text = element.querySelector(".cassis-pdf-text-layer");
    if (!canvas || !text) return;
    text.replaceChildren();
    const scaledViewport = page.getViewport({ scale: ratio * scale() });
    const renderTask = page.render({
      canvasContext: canvas.getContext("2d"),
      viewport: scaledViewport,
    });
    const textLayer = new TextLayer({
      textContentSource: await page.getTextContent(),
      viewport: scaledViewport,
      container: text,
    });
    await Promise.all([renderTask.promise, textLayer.render()]);
    if (element.lastPage && element.lastPage !== page) {
      // Release the parsed resources of the page this element showed
      // before; PDF.js would otherwise keep every visited page in memory.
      element.lastPage.cleanup();
    }
    element.lastPage = page;
    element.rendered = true;
    element.renderedPage = pageNumber;
    element.removeAttribute("data-render-error");
    /* c8 ignore next 5 */
  } catch (err) {
    // One page failing to paint does not make the document unreadable.
    element.setAttribute("data-render-error", "");
    console.error(err);
  }
}
